/**
 * FilterHeader Component
 * Reusable filter header for all dashboard views (board, list, gantt, calendar)
 * Displays filters in hierarchical order: Project, Sprint, Epic (Gantt only), Status, Task Type, Label, Priority, Assignee, Start Date, Due Date, Sorting
 */
import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  Avatar,
  Chip
} from '@mui/material';
import {
  Search,
  DirectionsRun,
  Category,
  Flag,
  Person,
  Sort,
  Timeline
} from '@mui/icons-material';
import ManagedFilterSelect from './ManagedFilterSelect';
import ScrumSprintStateSelect from './ScrumSprintStateSelect';
import PopperSelect from '../../shared/components/PopperSelect';
import PopperDateField from '../../shared/components/PopperDateField';
import EpicSelector from './EpicSelector';
import OverflowTooltip from '../../shared/OverflowTooltip';
import FilterPanel from './FilterPanel';
import { FILTER_VALUES, SCRUM_BACKLOG_SENTINEL, CHARACTER_LIMITS, VIEW_MODES } from '../../shared/appConstants';
import { COLORS } from '../../shared/colors';
import { ICONS } from '../../shared/icons';
import { getTaskTypeIcon } from '../../shared/iconUtils';
import { lightenColor } from './calendar-view/calendarColorUtils';
import epicFilterIcon from '../../assets/images/epic_filter_icon.svg';
import './FilterHeader.scss';

const EMPTY_SPRINT_SELECTION = [];

export const findDropdownOptionById = (items = [], id) => (
  items?.find?.(item => String(item?.id) === String(id))
);

export const renderSingleDropdownValue = (value, items, allLabel, unresolvedLabel) => {
  if (!value || value === FILTER_VALUES.ALL) return allLabel;
  return findDropdownOptionById(items, value)?.name || unresolvedLabel;
};

export const renderProjectDropdownValue = (value, projects, selectProjectLabel, unresolvedLabel) => {
  if (!value || value === FILTER_VALUES.ALL) return selectProjectLabel;
  return findDropdownOptionById(projects, value)?.name || unresolvedLabel;
};

const SelectedValueText = ({ children, reserveArrowSpace = false, color }) => (
  <Box
    component="span"
    className="filter-selected-value-text"
    sx={{
      ...(reserveArrowSpace ? {
        width: 'calc(100% - 12px)',
        maxWidth: 'calc(100% - 12px)'
      } : {}),
      ...(color ? { color } : {})
    }}
  >
    {children}
  </Box>
);

SelectedValueText.propTypes = {
  children: PropTypes.node,
  reserveArrowSpace: PropTypes.bool,
  color: PropTypes.string,
};

const FilterValueChips = ({
  selected = [],
  items = [],
  allLabel,
  unresolvedLabel,
  maxVisible = 2,
  getColor = (item) => item?.colour || COLORS.gray[400]
}) => {
  const selectedValues = Array.isArray(selected) ? selected : [selected];
  if (selectedValues?.includes?.(FILTER_VALUES.ALL) || selectedValues?.length === 0) {
    return <SelectedValueText>{allLabel}</SelectedValueText>;
  }

  const visibleValues = selectedValues?.slice?.(0, maxVisible) || [];
  const remainingCount = Math.max((selectedValues?.length || 0) - maxVisible, 0);
  const getOption = (id) => findDropdownOptionById(items, id);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      {visibleValues?.map?.((value) => {
        const option = getOption(value);
        const label = option?.name || unresolvedLabel;
        const color = getColor(option);
        const bgColor = color !== COLORS.gray[400] ? lightenColor(color, 85) : COLORS.gray[100];
        
        return (
          <Chip
            key={value}
            label={label}
            size="small"
            sx={{
              bgcolor: bgColor,
              color: color !== COLORS.gray[400] ? color : COLORS.gray[600],
              border: `1px solid ${color}`,
              borderRadius: '24px',
              fontSize: '11px',
              height: 22,
              fontWeight: 500,
              flexShrink: 1,
              minWidth: 0,
              maxWidth: '120px',
              '& .MuiChip-label': {
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                padding: '0 8px',
                lineHeight: '20px'
              }
            }}
          />
        );
      })}
      {remainingCount > 0 && (
        <Chip
          label={`+${remainingCount}`}
          size="small"
          sx={{
            bgcolor: COLORS.gray[100],
            color: COLORS.gray[600],
            border: `1px solid ${COLORS.gray[300]}`,
            borderRadius: '24px',
            fontSize: '11px',
            height: 22,
            fontWeight: 500,
            minWidth: 'auto',
            flexShrink: 1,
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100px',
              padding: '0 8px',
              lineHeight: '20px',
              display: 'block'
            }
          }}
        />
      )}
    </Box>
  );
};

const DateFilterField = ({
  value,
  onChange,
  onFocus,
  ariaLabel,
  icon,
}) => (
  <PopperDateField
    value={value || ''}
    onChange={onChange}
    onFocus={onFocus}
    ariaLabel={ariaLabel}
    icon={icon}
  />
);

DateFilterField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  ariaLabel: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

const isBacklogOnlySprintDefault = (selection) => {
  const selected = Array.isArray(selection) ? selection.map(String) : [];
  return selected.length === 1 && selected[0] === SCRUM_BACKLOG_SENTINEL;
};

const FilterHeader = ({
  savedFilterActions,
  savedFilterRevision = 0,
  // Filter values
  searchTerm,
  selectedProject,
  selectedSprint,
  selectedTaskType,
  selectedLabels,
  selectedPriority,
  selectedAssignee,
  selectedCreatedBy,
  selectedStatus,
  selectedEpic,
  startDate,
  dueDate,
  selectedSort,
  
  // Filter change handlers
  onSearchChange,
  onProjectChange,
  onSprintChange,
  onTaskTypeChange,
  onLabelsChange,
  onPriorityChange,
  onAssigneeChange,
  onCreatedByChange,
  onStatusChange,
  onEpicChange,
  onStartDateChange,
  onDueDateChange,
  onSortChange,
  
  // Dropdown data
  projectDropdownsByUser,
  dropdowns,
  dropdownsLoading = false,
  isScrumProject = false,
  
  // Create handlers for managed filters
  onCreatePriority,
  onUpdatePriority,
  onDeletePriority,
  onCreateTaskType,
  onUpdateTaskType,
  onDeleteTaskType,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  onCreateSprint,
  onUpdateSprint,
  onDeleteSprint,
  
  // Epic selector props
  searchEpics,
  
  // View mode
  viewMode = VIEW_MODES.NOTIFICATIONS,
  
  // Permissions
  canManageTicketAttributes,
  
  // Clear filters
  onClearFilters,
  
  // Utility functions
  getPriorityColor,
  getStatusColor,
  
  // Date filter focus handler (for notifications)
  onDateFilterFocus = () => {},

  // Reports-specific: pre-built filter descriptors + dropdowns
  reportFilterDropdowns = {},
  reportVisibleFilters = [],
  reportAvailableFilters = [],
  onReportFilterClick,
  onReportRemoveFilter,
  reportActions,
  active,
  appliedCount,
}) => {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  React.useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  const resolvedAppliedCount = useMemo(() => {
    if (appliedCount !== undefined) return appliedCount;
    if (viewMode === VIEW_MODES.NOTIFICATIONS) {
      let count = 0;
      if (selectedProject !== FILTER_VALUES.ALL && selectedProject !== '') count++;
      if (selectedAssignee !== FILTER_VALUES.ALL && selectedAssignee !== '' && selectedAssignee !== 'all') count++;
      if (startDate) count++;
      if (dueDate) count++;
      if (selectedSort && selectedSort !== 'recent') count++;
      return count;
    }
    // Dashboard views (board, list, gantt, calendar)
    let count = 0;
    if (selectedSprint && selectedSprint.length > 0 && !selectedSprint.includes(FILTER_VALUES.ALL) && !isBacklogOnlySprintDefault(selectedSprint)) count++;
    if (selectedTaskType && selectedTaskType !== FILTER_VALUES.ALL) count++;
    if (selectedLabels && selectedLabels.length > 0 && !selectedLabels.includes(FILTER_VALUES.ALL)) count++;
    if (selectedPriority && selectedPriority !== FILTER_VALUES.ALL) count++;
    if (selectedAssignee && selectedAssignee !== FILTER_VALUES.ALL) count++;
    if (selectedCreatedBy && selectedCreatedBy !== FILTER_VALUES.ALL) count++;
    if (selectedStatus && selectedStatus !== FILTER_VALUES.ALL) count++;
    if (selectedEpic && selectedEpic !== FILTER_VALUES.ALL && selectedEpic !== '' && viewMode === VIEW_MODES.GANTT) count++;
    if (startDate) count++;
    if (dueDate) count++;
    if (selectedSort && selectedSort !== 'NEWEST_FIRST' && viewMode !== VIEW_MODES.GANTT && viewMode !== VIEW_MODES.CALENDAR) count++;
    return count;
  }, [
    appliedCount,
    viewMode,
    selectedProject,
    selectedAssignee,
    startDate,
    dueDate,
    selectedSort,
    selectedSprint,
    selectedTaskType,
    selectedLabels,
    selectedPriority,
    selectedCreatedBy,
    selectedStatus,
    selectedEpic
  ]);
  const unresolvedFilterLabel = dropdownsLoading ? t('dashboard.loading') : t('dashboard.unavailableFilterValue');
  const renderStatusValue = React.useCallback((value) => {
    if (!value || value === FILTER_VALUES.ALL) return <SelectedValueText>{t('dashboard.allStatus')}</SelectedValueText>;
    return (
      <FilterValueChips
        selected={[value]}
        items={dropdowns?.statuses}
        allLabel={t('dashboard.allStatus')}
        unresolvedLabel={unresolvedFilterLabel}
        getColor={(item) => getStatusColor(item?.id)}
      />
    );
  }, [dropdowns?.statuses, t, unresolvedFilterLabel, getStatusColor]);
  const renderAssigneeValue = React.useCallback((value) => (
    value && value !== FILTER_VALUES.ALL ? (
      <SelectedValueText reserveArrowSpace>
        {findDropdownOptionById(dropdowns?.assignees, value)?.name || unresolvedFilterLabel}
      </SelectedValueText>
    ) : <SelectedValueText>{t('dashboard.allPeople')}</SelectedValueText>
  ), [dropdowns?.assignees, t, unresolvedFilterLabel]);
  const renderSprintValue = React.useCallback((selected) => {
    // Ticket filters store sprint values as an array, but Scrum uses a single-select
    // dropdown. Normalizing here keeps the shared chip renderer happy for both modes.
    const selectedValues = Array.isArray(selected) ? selected : [selected];
    return (
      <FilterValueChips
        selected={selectedValues}
        items={isScrumProject ? dropdowns?.scrum_sprints : dropdowns?.sprints}
        allLabel={isScrumProject ? t('dashboard.allScrumSprints', 'All Scrum Sprints') : t('dashboard.allSprints')}
        unresolvedLabel={unresolvedFilterLabel}
        getColor={(item) => item?.colour || COLORS.gray[400]}
      />
    );
  }, [dropdowns?.scrum_sprints, dropdowns?.sprints, isScrumProject, t, unresolvedFilterLabel]);
  const renderLabelsValue = React.useCallback((selected) => (
    <FilterValueChips
      selected={selected}
      items={dropdowns?.labels}
      allLabel={t('dashboard.allLabels')}
      unresolvedLabel={unresolvedFilterLabel}
      getColor={(item) => item?.colour || COLORS.gray[400]}
    />
  ), [dropdowns?.labels, t, unresolvedFilterLabel]);
  const renderPriorityValue = React.useCallback((value) => {
    if (!value || value === FILTER_VALUES.ALL) return <SelectedValueText>{t('dashboard.allPriorities')}</SelectedValueText>;
    return (
      <FilterValueChips
        selected={[value]}
        items={dropdowns?.priorities}
        allLabel={t('dashboard.allPriorities')}
        unresolvedLabel={unresolvedFilterLabel}
        getColor={(item) => getPriorityColor(item)}
      />
    );
  }, [dropdowns?.priorities, t, unresolvedFilterLabel]);
  const renderColorDotItem = React.useCallback((item) => (
    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          bgcolor: item?.colour || COLORS.primary,
          flexShrink: 0
        }}
      />
      <OverflowTooltip title={item?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
    </Box>
  ), [unresolvedFilterLabel]);
  const renderTaskTypeItem = React.useCallback((item) => (
    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
      <img src={getTaskTypeIcon(item?.name)} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
      <OverflowTooltip title={item?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
    </Box>
  ), [unresolvedFilterLabel]);
  const renderTaskTypeValue = React.useCallback((value) => {
    if (!value || value === FILTER_VALUES.ALL) return <SelectedValueText>{t('dashboard.allTypes')}</SelectedValueText>;

    const item = findDropdownOptionById(dropdowns?.types, value);
    const label = item?.name || unresolvedFilterLabel;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, maxWidth: '100%', overflow: 'hidden', color: COLORS.black }}>
        <img src={getTaskTypeIcon(item?.name)} alt="" style={{ width: 16, height: 16, flexShrink: 0 }} />
        <Box
          component="span"
          sx={{
            display: 'block',
            minWidth: 0,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: COLORS.black,
            fontWeight: 400
          }}
        >
          {label}
        </Box>
      </Box>
    );
  }, [dropdowns?.types, t, unresolvedFilterLabel]);
  const renderPriorityItem = React.useCallback((item) => (
    <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
      <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: getPriorityColor(item) }}>
        <Flag sx={{ fontSize: 12 }} />
      </Avatar>
      <OverflowTooltip title={item?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
    </Box>
  ), [unresolvedFilterLabel]);
  const getAppliedFilterIds = () => {
    const initial = [];
    
    // For notifications view, only show sort by default
    if (viewMode === VIEW_MODES.NOTIFICATIONS) {
      initial.push('sort');
      if (selectedProject !== FILTER_VALUES.ALL && selectedProject !== '') initial.push('project');
      if (selectedAssignee !== FILTER_VALUES.ALL && selectedAssignee !== '' && selectedAssignee !== 'all') initial.push('assignee');
      if (startDate) initial.push('startDate');
      if (dueDate) initial.push('dueDate');
      return initial;
    }
    
    // For other views, show all applicable filters
    initial.push('project');
    if (selectedEpic !== FILTER_VALUES.ALL && selectedEpic !== '') initial.push('epic');
    if (selectedSprint?.length > 0 && !selectedSprint?.includes?.(FILTER_VALUES.ALL) && !isBacklogOnlySprintDefault(selectedSprint)) initial.push('sprint');
    if (selectedStatus !== FILTER_VALUES.ALL && selectedStatus !== '') initial.push('status');
    if (selectedTaskType !== FILTER_VALUES.ALL && selectedTaskType !== '') initial.push('taskType');
    if (selectedLabels?.length > 0 && !selectedLabels?.includes?.(FILTER_VALUES.ALL)) initial.push('labels');
    if (selectedPriority !== FILTER_VALUES.ALL && selectedPriority !== '') initial.push('priority');
    if (selectedAssignee !== FILTER_VALUES.ALL && selectedAssignee !== '') initial.push('assignee');
    if (selectedCreatedBy !== FILTER_VALUES.ALL && selectedCreatedBy !== '') initial.push('createdBy');
    if (startDate) initial.push('startDate');
    if (dueDate) initial.push('dueDate');
    if (selectedSort && selectedSort !== 'NEWEST_FIRST') initial.push('sort');
    return initial;
  };
  const [visibleFilters, setVisibleFilters] = React.useState(getAppliedFilterIds);

  // Restore the visible fields without remounting FilterPanel and losing its open state.
  React.useEffect(() => {
    if (!savedFilterRevision) return;
    setVisibleFilters(getAppliedFilterIds());
    clearTimeout(searchTimeoutRef.current);
    if (searchInputRef.current) searchInputRef.current.value = searchTerm || '';
    // Only applying/resetting a saved filter should replace the user's visible fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilterRevision]);

  React.useEffect(() => {
    if (viewMode === VIEW_MODES.NOTIFICATIONS) {
      const isProjectApplied = selectedProject !== FILTER_VALUES.ALL && selectedProject !== '';
      
      setVisibleFilters(prev => {
        const isProjectVisible = prev.includes('project');
        if (isProjectApplied && !isProjectVisible) {
          return ['project', ...prev];
        } else if (!isProjectApplied && isProjectVisible) {
          return prev.filter(id => id !== 'project');
        }
        return prev;
      });
    }
  }, [selectedProject, viewMode]);

  const handleAddFilterSelect = (filterId) => {
    if (!visibleFilters?.includes?.(filterId)) {
      // For notifications, maintain specific order: sort, assignee, startDate, dueDate
      if (viewMode === VIEW_MODES.NOTIFICATIONS) {
        const notificationOrder = ['sort', 'assignee', 'startDate', 'dueDate'];
        const getOrderIndex = (id) => {
          const index = notificationOrder.indexOf(id);
          return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        };
        const newFilters = [...visibleFilters, filterId];
        const sortedFilters = newFilters?.slice?.()?.sort?.((a, b) => {
          return getOrderIndex(a) - getOrderIndex(b);
        }) || [];
        setVisibleFilters(sortedFilters);
      } else {
        setVisibleFilters(prev => [...prev, filterId]);
      }
    }
  };

  const removeFilter = (filterId) => {
    setVisibleFilters(prev => prev?.filter?.(id => id !== filterId) || []);
    // Clear the filter value if being removed
    if (filterId === 'epic') onEpicChange(FILTER_VALUES.ALL);
    if (filterId === 'sprint') {
      onSprintChange({ target: { value: isScrumProject ? [SCRUM_BACKLOG_SENTINEL] : [FILTER_VALUES.ALL], isDefaultSelection: true } });
    }
    if (filterId === 'status') onStatusChange({ target: { value: FILTER_VALUES.ALL } });
    if (filterId === 'taskType') onTaskTypeChange({ target: { value: FILTER_VALUES.ALL } });
    if (filterId === 'labels') onLabelsChange({ target: { value: [FILTER_VALUES.ALL] } });
    if (filterId === 'priority') onPriorityChange({ target: { value: FILTER_VALUES.ALL } });
    if (filterId === 'assignee') onAssigneeChange({ target: { value: FILTER_VALUES.ALL } });
    if (filterId === 'createdBy') onCreatedByChange({ target: { value: FILTER_VALUES.ALL } });
    if (filterId === 'startDate') onStartDateChange({ target: { value: '' } });
    if (filterId === 'dueDate') onDueDateChange({ target: { value: '' } });
    if (filterId === 'sort') onSortChange({ target: { value: viewMode === VIEW_MODES.NOTIFICATIONS ? 'recent' : 'NEWEST_FIRST' } });
  };

  const handleClearAll = () => {
    onClearFilters?.();
    if (viewMode === VIEW_MODES.NOTIFICATIONS) {
      setVisibleFilters(['sort']);
    } else {
      setVisibleFilters(['project']);
    }
  };

  const availableFilters = useMemo(() => [
    ...(viewMode === VIEW_MODES.NOTIFICATIONS ? [
      { id: 'project', label: t('dashboard.project'), icon: ICONS.dropdownProject },
      { id: 'sort', label: t('dashboard.sort.label'), icon: <Sort sx={{ fontSize: 18 }} /> },
      { id: 'assignee', label: t('dashboard.assignee'), icon: <Person sx={{ fontSize: 18 }} /> },
      { id: 'startDate', label: t('dashboard.startDate'), icon: ICONS.dateRange || ICONS.dropdownStartDate },
      { id: 'dueDate', label: t('dashboard.dueDate'), icon: ICONS.dropdownDueDate }
    ] : [
      ...(viewMode === VIEW_MODES.GANTT ? [{ id: 'epic', label: t('dashboard.epic'), icon: epicFilterIcon }] : []),
      { id: 'sprint', label: isScrumProject ? t('dashboard.scrumSprints', 'Scrum Sprints') : t('taskModal.sprint'), icon: isScrumProject ? ICONS.dropdownScrumSprint : <DirectionsRun sx={{ fontSize: 18 }} /> },
      { id: 'status', label: t('dashboard.status'), icon: <Timeline sx={{ fontSize: 18 }} /> },
      { id: 'taskType', label: t('dashboard.taskType'), icon: <Category sx={{ fontSize: 18 }} /> },
      { id: 'labels', label: t('dashboard.labels'), icon: <Category sx={{ fontSize: 18 }} /> },
      { id: 'priority', label: t('dashboard.priority'), icon: <Flag sx={{ fontSize: 18 }} /> },
      { id: 'assignee', label: t('dashboard.assignee'), icon: <Person sx={{ fontSize: 18 }} /> },
      { id: 'createdBy', label: t('dashboard.createdBy'), icon: <Person sx={{ fontSize: 18 }} /> },
      { id: 'startDate', label: t('dashboard.startDate'), icon: ICONS.dateRange || ICONS.dropdownStartDate },
      { id: 'dueDate', label: t('dashboard.dueDate'), icon: ICONS.dropdownDueDate },
      ...(viewMode !== VIEW_MODES.GANTT && viewMode !== VIEW_MODES.CALENDAR ? [{ id: 'sort', label: t('dashboard.sort.label'), icon: <Sort sx={{ fontSize: 18 }} /> }] : [])
    ])
  ], [isScrumProject, t, viewMode]);

  const notificationFilters = useMemo(() => {
    if (viewMode !== VIEW_MODES.NOTIFICATIONS) return [];

    return availableFilters.map((filter) => {
      const common = {
        ...filter,
        active: visibleFilters.includes(filter.id),
        visible: visibleFilters.includes(filter.id),
      };

      if (filter.id === 'project') {
        return {
          ...common,
          disableRemove: true,
          renderDropdown: () => (
            <PopperSelect
              value={selectedProject}
              onChange={onProjectChange}
              displayEmpty
              renderValue={(value) => {
                if (!value || value === FILTER_VALUES.ALL) return <SelectedValueText>{t('dashboard.allProjects')}</SelectedValueText>;
                return (
                  <SelectedValueText reserveArrowSpace>
                    {findDropdownOptionById(projectDropdownsByUser, value)?.name || unresolvedFilterLabel}
                  </SelectedValueText>
                );
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
            >
              <MenuItem value={FILTER_VALUES.ALL}>{t('dashboard.allProjects')}</MenuItem>
              {projectDropdownsByUser?.map?.((project) => (
                <MenuItem key={project.id} value={project.id}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.primary }}>
                      {project?.name?.charAt(0) || t('dashboard.projectInitial')}
                    </Avatar>
                    <OverflowTooltip title={project?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                  </Box>
                </MenuItem>
              ))}
            </PopperSelect>
          )
        };
      }

      if (filter.id === 'sort') {
        return {
          ...common,
          renderDropdown: () => (
            <PopperSelect
              value={selectedSort || 'recent'}
              onChange={onSortChange}
              displayEmpty
              renderValue={(value) => (
                <SelectedValueText>
                  {value === 'oldest'
                    ? t('notifications.sort.options.oldest', 'Oldest First')
                    : t('notifications.sort.options.recent', 'Most Recent')}
                </SelectedValueText>
              )}
              MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
            >
              <MenuItem value="recent">{t('notifications.sort.options.recent', 'Most Recent')}</MenuItem>
              <MenuItem value="oldest">{t('notifications.sort.options.oldest', 'Oldest First')}</MenuItem>
            </PopperSelect>
          )
        };
      }

      if (filter.id === 'assignee') {
        return {
          ...common,
          renderDropdown: () => (
            <PopperSelect
              value={selectedAssignee || 'all'}
              onChange={onAssigneeChange}
              displayEmpty
              renderValue={renderAssigneeValue}
              MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
            >
              <MenuItem value="all">{t('dashboard.allPeople')}</MenuItem>
              {dropdowns?.assignees?.map?.((assignee) => (
                <MenuItem key={assignee.id} value={assignee.id}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.green[500] }}>
                      {assignee?.name?.charAt(0) || t('dashboard.personInitial')}
                    </Avatar>
                    <OverflowTooltip title={assignee?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                  </Box>
                </MenuItem>
              ))}
            </PopperSelect>
          )
        };
      }

      if (filter.id === 'startDate' || filter.id === 'dueDate') {
        const isStartDate = filter.id === 'startDate';
        return {
          ...common,
          renderDropdown: () => (
            <DateFilterField
              value={isStartDate ? (startDate || '') : (dueDate || '')}
              onChange={isStartDate ? onStartDateChange : onDueDateChange}
              onFocus={onDateFilterFocus}
              ariaLabel={isStartDate ? t('dashboard.startDate') : t('dashboard.dueDate')}
              icon={filter.icon}
            />
          )
        };
      }

      return common;
    });
  }, [
    availableFilters,
    dropdowns?.assignees,
    dueDate,
    onAssigneeChange,
    onDateFilterFocus,
    onDueDateChange,
    onProjectChange,
    projectDropdownsByUser,
    selectedProject,
    onSortChange,
    onStartDateChange,
    renderAssigneeValue,
    selectedAssignee,
    selectedSort,
    startDate,
    t,
    unresolvedFilterLabel,
    viewMode,
    visibleFilters
  ]);
  const handleSearchChange = (e) => {
    const value = e.target.value;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value === '') {
      onSearchChange('');
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    }
  };

  return (
    <Box className="filter-header-container">
      <Box className="filter-header-content">
        {viewMode === VIEW_MODES.NOTIFICATIONS ? (
          <FilterPanel
            filters={notificationFilters}
            onFilterClick={(filterId) => {
              if (!visibleFilters.includes(filterId)) {
                handleAddFilterSelect(filterId);
              }
            }}
            onRemoveFilter={removeFilter}
            onClearAll={handleClearAll}
            hasClearable={
              visibleFilters.length > 0 ||
              (selectedProject !== FILTER_VALUES.ALL && selectedProject !== '') ||
              (selectedAssignee !== FILTER_VALUES.ALL && selectedAssignee !== '' && selectedAssignee !== 'all') ||
              Boolean(startDate) ||
              Boolean(dueDate) ||
              (selectedSort || 'recent') !== 'recent'
            }
            active={
              active !== undefined ? active : (
                (selectedProject !== FILTER_VALUES.ALL && selectedProject !== '') ||
                (selectedAssignee !== FILTER_VALUES.ALL && selectedAssignee !== '' && selectedAssignee !== 'all') ||
                Boolean(startDate) ||
                Boolean(dueDate) ||
                (selectedSort && selectedSort !== 'recent')
              )
            }
            appliedCount={resolvedAppliedCount}
          />
        ) : viewMode === VIEW_MODES.REPORTS ? (
          <>
            <FilterPanel
              filters={reportAvailableFilters.map((f) => ({
                ...f,
                active: reportVisibleFilters.includes(f.id),
                visible: reportVisibleFilters.includes(f.id),
                disableRemove: f.id === 'project',
                renderDropdown: reportFilterDropdowns[f.id],
              }))}
              onFilterClick={onReportFilterClick}
              onRemoveFilter={onReportRemoveFilter}
              onClearAll={handleClearAll}
              hasClearable={reportVisibleFilters.length > 0}
              active={active}
              appliedCount={resolvedAppliedCount}
            />
            {reportActions}
          </>
        ) : (
          <>
            {/* Search bar */}
            <TextField
              key={selectedProject}
              size="small"
              placeholder="Search"
              defaultValue={searchTerm}
              inputRef={searchInputRef}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 20, color: COLORS.gray[700] }} />
                  </InputAdornment>
                )
              }}
              className="filter-header-search"
            />

            {/* Filter funnel panel */}
            <FilterPanel
              actions={savedFilterActions}
              filters={[
                {
                  id: 'project',
                  label: t('dashboard.project'),
                  icon: ICONS.dropdownProject,
                  active: visibleFilters.includes('project'),
                  visible: visibleFilters.includes('project'),
                  disableRemove: true,
                  renderDropdown: () => (
                    <PopperSelect
                      value={selectedProject}
                      onChange={onProjectChange}
                      displayEmpty
                      renderValue={(value) => {
                        if (!value || value === FILTER_VALUES.ALL) return <SelectedValueText>{t('dashboard.allProjects')}</SelectedValueText>;
                        return (
                          <SelectedValueText reserveArrowSpace>
                            {findDropdownOptionById(projectDropdownsByUser, value)?.name || unresolvedFilterLabel}
                          </SelectedValueText>
                        );
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
                    >
                      {projectDropdownsByUser?.length === 0 && (
                        <MenuItem value="all">{t('dashboard.allProjects')}</MenuItem>
                      )}
                      {projectDropdownsByUser
                        ?.map?.((project) => (
                        <MenuItem key={project.id} value={project.id}>
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.primary }}>
                              {project?.name?.charAt(0) || t('dashboard.projectInitial')}
                            </Avatar>
                            <OverflowTooltip title={project?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                          </Box>
                        </MenuItem>
                      ))}
                    </PopperSelect>
                  )
                },
                ...(viewMode === VIEW_MODES.GANTT ? [{
                  id: 'epic',
                  label: t('dashboard.epic'),
                  icon: epicFilterIcon,
                  active: visibleFilters.includes('epic'),
                  visible: visibleFilters.includes('epic'),
                  renderDropdown: () => (
                    <EpicSelector
                      value={selectedEpic}
                      onChange={onEpicChange}
                      projectId={selectedProject}
                      searchTickets={searchEpics}
                      loadInitialEpics={true}
                    />
                  )
                }] : []),
                {
                  id: 'sprint',
                  label: isScrumProject ? t('dashboard.scrumSprints', 'Scrum Sprints') : t('taskModal.sprint'),
                  icon: isScrumProject ? ICONS.dropdownScrumSprint : (ICONS.dropdownSprint || ICONS.sprint),
                  active: visibleFilters.includes('sprint'),
                  visible: visibleFilters.includes('sprint'),
                  renderDropdown: () => isScrumProject ? (
                    <ScrumSprintStateSelect
                      sprints={dropdowns?.scrum_sprints || []}
                      value={selectedSprint?.includes(FILTER_VALUES.ALL) ? FILTER_VALUES.ALL : selectedSprint?.join(',')}
                      onChange={onSprintChange}
                      allLabel={t('dashboard.allScrumSprints', 'All Scrum Sprints')}
                    />
                  ) : (
                    <ManagedFilterSelect
                      label="sprint"
                      menuPaperStyle={{ maxHeight: 200, width: 1, overflow: 'auto' }}
                      multiple
                      deferChanges
                      value={selectedSprint?.includes(FILTER_VALUES.ALL) ? EMPTY_SPRINT_SELECTION : selectedSprint}
                      onChange={onSprintChange}
                      renderValue={renderSprintValue}
                      displayEmpty
                      allOptionLabel={isScrumProject ? t('dashboard.allScrumSprints', 'All Scrum Sprints') : t('dashboard.allSprints')}
                      unresolvedOptionLabel={unresolvedFilterLabel}
                      items={dropdowns?.sprints || []}
                      allowCreate={selectedProject !== FILTER_VALUES.ALL}
                      canManage={canManageTicketAttributes}
                      createText={t('taskModal.createNewSprint')}
                      createPlaceholder={t('taskModal.enterSprintName')}
                      maxLength={CHARACTER_LIMITS.LABEL_NAME}
                      withColor
                      onCreate={onCreateSprint}
                      onUpdate={onUpdateSprint}
                      onDelete={onDeleteSprint}
                      isItemSelected={(item) => selectedSprint?.includes?.(item?.id)}
                      renderItemLeft={renderColorDotItem}
                    />
                  )
                },
                {
                  id: 'status',
                  label: t('dashboard.status'),
                  icon: ICONS.dropdownStatus || ICONS.status,
                  active: visibleFilters.includes('status'),
                  visible: visibleFilters.includes('status'),
                  renderDropdown: () => (
                    <PopperSelect
                      value={selectedStatus}
                      onChange={onStatusChange}
                      displayEmpty
                      renderValue={renderStatusValue}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
                    >
                      <MenuItem value="all">{t('dashboard.allStatus')}</MenuItem>
                      {dropdowns?.statuses?.map?.((status) => (
                        <MenuItem key={status.id} value={status.id}>
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: getStatusColor(status?.id) }}>
                              <Timeline sx={{ fontSize: 12 }} />
                            </Avatar>
                            <OverflowTooltip title={status?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                          </Box>
                        </MenuItem>
                      ))}
                    </PopperSelect>
                  )
                },
                {
                  id: 'taskType',
                  label: t('dashboard.taskType'),
                  icon: ICONS.dropdownTaskType,
                  active: visibleFilters.includes('taskType'),
                  visible: visibleFilters.includes('taskType'),
                  renderDropdown: () => (
                    <ManagedFilterSelect
                      label="taskType"
                      menuPaperStyle={{ maxHeight: 200, width: 1, overflow: 'auto' }}
                      value={selectedTaskType}
                      onChange={onTaskTypeChange}
                      displayEmpty
                      renderValue={renderTaskTypeValue}
                      allOptionLabel={t('dashboard.allTypes')}
                      unresolvedOptionLabel={unresolvedFilterLabel}
                      items={dropdowns?.types || []}
                      allowCreate={selectedProject !== FILTER_VALUES.ALL}
                      canManage={canManageTicketAttributes}
                      createText={t('taskModal.createNewTaskType')}
                      createPlaceholder={t('taskModal.enterTaskTypeName')}
                      maxLength={CHARACTER_LIMITS.TASK_TYPE_NAME}
                      onCreate={onCreateTaskType}
                      onUpdate={onUpdateTaskType}
                      onDelete={onDeleteTaskType}
                      isItemSelected={(item) => selectedTaskType === item?.id}
                      renderItemLeft={renderTaskTypeItem}
                    />
                  )
                },
                {
                  id: 'labels',
                  label: t('dashboard.labels'),
                  icon: ICONS.dropdownLabels || ICONS.dropdownLabel,
                  active: visibleFilters.includes('labels'),
                  visible: visibleFilters.includes('labels'),
                  renderDropdown: () => (
                    <ManagedFilterSelect
                      label="label"
                      menuPaperStyle={{ maxHeight: 200, width: 1, overflow: 'auto' }}
                      multiple
                      value={selectedLabels}
                      onChange={onLabelsChange}
                      renderValue={renderLabelsValue}
                      displayEmpty
                      allOptionLabel={t('dashboard.allLabels')}
                      unresolvedOptionLabel={unresolvedFilterLabel}
                      items={dropdowns?.labels || []}
                      allowCreate={selectedProject !== FILTER_VALUES.ALL}
                      canManage={canManageTicketAttributes}
                      createText={t('taskModal.createNewLabel')}
                      createPlaceholder={t('taskModal.enterLabelName')}
                      maxLength={CHARACTER_LIMITS.LABEL_NAME}
                      withColor
                      onCreate={onCreateLabel}
                      onUpdate={onUpdateLabel}
                      onDelete={onDeleteLabel}
                      isItemSelected={(item) => selectedLabels?.includes?.(item?.id)}
                      renderItemLeft={renderColorDotItem}
                    />
                  )
                },
                {
                  id: 'priority',
                  label: t('dashboard.priority'),
                  icon: ICONS.dropdownPriority,
                  active: visibleFilters.includes('priority'),
                  visible: visibleFilters.includes('priority'),
                  renderDropdown: () => (
                    <ManagedFilterSelect
                      label="priority"
                      menuPaperStyle={{ maxHeight: 200, width: 1, overflow: 'auto' }}
                      value={selectedPriority}
                      onChange={onPriorityChange}
                      renderValue={renderPriorityValue}
                      displayEmpty
                      allOptionLabel={t('dashboard.allPriorities')}
                      unresolvedOptionLabel={unresolvedFilterLabel}
                      items={dropdowns?.priorities || []}
                      allowCreate={selectedProject !== FILTER_VALUES.ALL}
                      canManage={canManageTicketAttributes}
                      createText={t('taskModal.createNewPriority')}
                      createPlaceholder={t('taskModal.enterPriorityName')}
                      maxLength={CHARACTER_LIMITS.PRIORITY_NAME}
                      withColor
                      onCreate={onCreatePriority}
                      onUpdate={onUpdatePriority}
                      onDelete={onDeletePriority}
                      isItemSelected={(item) => selectedPriority === item?.id}
                      renderItemLeft={renderPriorityItem}
                    />
                  )
                },
                {
                  id: 'assignee',
                  label: t('dashboard.assignee'),
                  icon: ICONS.dropdownAssignee,
                  active: visibleFilters.includes('assignee'),
                  visible: visibleFilters.includes('assignee'),
                  renderDropdown: () => (
                    <PopperSelect
                      value={selectedAssignee}
                      onChange={onAssigneeChange}
                      displayEmpty
                      renderValue={renderAssigneeValue}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
                    >
                      <MenuItem value="all">{t('dashboard.allPeople')}</MenuItem>
                      {dropdowns?.assignees?.map?.((assignee) => (
                        <MenuItem key={assignee.id} value={assignee.id}>
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.green[500] }} >
                              {assignee?.name?.charAt(0) || t('dashboard.personInitial')}
                            </Avatar>
                            <OverflowTooltip title={assignee?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                          </Box>
                        </MenuItem>
                      ))}
                    </PopperSelect>
                  )
                },
                {
                  id: 'createdBy',
                  label: t('dashboard.createdBy'),
                  icon: ICONS.dropdownAssignee || ICONS.dropdownCreatedBy,
                  active: visibleFilters.includes('createdBy'),
                  visible: visibleFilters.includes('createdBy'),
                  renderDropdown: () => (
                    <PopperSelect
                      value={selectedCreatedBy}
                      onChange={onCreatedByChange}
                      displayEmpty
                      renderValue={renderAssigneeValue}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
                    >
                      <MenuItem value="all">{t('dashboard.allPeople')}</MenuItem>
                      {dropdowns?.assignees?.map?.((assignee) => (
                        <MenuItem key={assignee.id} value={assignee.id}>
                          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0, flex: 1 }}>
                            <Avatar sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: COLORS.green[500] }} >
                              {assignee?.name?.charAt(0) || t('dashboard.personInitial')}
                            </Avatar>
                            <OverflowTooltip title={assignee?.name || unresolvedFilterLabel} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
                          </Box>
                        </MenuItem>
                      ))}
                    </PopperSelect>
                  )
                },
                {
                  id: 'startDate',
                  label: t('dashboard.startDate'),
                  icon: ICONS.dateRange || ICONS.dropdownStartDate,
                  active: visibleFilters.includes('startDate'),
                  visible: visibleFilters.includes('startDate'),
                  renderDropdown: () => (
                    <DateFilterField
                      value={startDate || ''}
                      onChange={onStartDateChange}
                      onFocus={onDateFilterFocus}
                      ariaLabel={t('dashboard.startDate')}
                      icon={ICONS.dateRange || ICONS.dropdownStartDate}
                    />
                  )
                },
                {
                  id: 'dueDate',
                  label: t('dashboard.dueDate'),
                  icon: ICONS.dropdownDueDate,
                  active: visibleFilters.includes('dueDate'),
                  visible: visibleFilters.includes('dueDate'),
                  renderDropdown: () => (
                    <DateFilterField
                      value={dueDate || ''}
                      onChange={onDueDateChange}
                      onFocus={onDateFilterFocus}
                      ariaLabel={t('dashboard.dueDate')}
                      icon={ICONS.dropdownDueDate}
                    />
                  )
                },
                ...(viewMode !== VIEW_MODES.GANTT && viewMode !== VIEW_MODES.CALENDAR ? [{
                  id: 'sort',
                  label: t('dashboard.sort.label'),
                  icon: ICONS.dropdownDateRange || ICONS.dropdownSort,
                  active: visibleFilters.includes('sort'),
                  visible: visibleFilters.includes('sort'),
                  renderDropdown: () => (
                    <PopperSelect
                      value={selectedSort || 'NEWEST_FIRST'}
                      onChange={onSortChange}
                      displayEmpty
                      renderValue={(value) => {
                        const sortLabels = {
                          RECENTLY_UPDATED: t('dashboard.sort.recentlyUpdated'),
                          PRIORITY: t('dashboard.sort.priority'),
                          NEWEST_FIRST: t('dashboard.sort.newestFirst'),
                          OLDEST_FIRST: t('dashboard.sort.oldestFirst'),
                          DUE_DATE: t('dashboard.sort.dueDate')
                        };

                        return <SelectedValueText>{sortLabels[value] || sortLabels.NEWEST_FIRST}</SelectedValueText>;
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 200, width: 1, overflow: 'auto' } } }}
                    >
                      <MenuItem value="RECENTLY_UPDATED">{t('dashboard.sort.recentlyUpdated')}</MenuItem>
                      <MenuItem value="PRIORITY">{t('dashboard.sort.priority')}</MenuItem>
                      <MenuItem value="NEWEST_FIRST">{t('dashboard.sort.newestFirst')}</MenuItem>
                      <MenuItem value="OLDEST_FIRST">{t('dashboard.sort.oldestFirst')}</MenuItem>
                      <MenuItem value="DUE_DATE">{t('dashboard.sort.dueDate')}</MenuItem>
                    </PopperSelect>
                  )
                }] : [])
              ]}
              onFilterClick={(filterId) => {
                if (!visibleFilters.includes(filterId)) {
                  handleAddFilterSelect(filterId);
                }
              }}
              onRemoveFilter={removeFilter}
              onClearAll={handleClearAll}
              hasClearable={visibleFilters.length > 0}
              active={
                active !== undefined ? active : (
                  (selectedSprint && selectedSprint.length > 0 && !selectedSprint.includes(FILTER_VALUES.ALL) && !isBacklogOnlySprintDefault(selectedSprint)) ||
                  (selectedTaskType && selectedTaskType !== FILTER_VALUES.ALL) ||
                  (selectedLabels && selectedLabels.length > 0 && !selectedLabels.includes(FILTER_VALUES.ALL)) ||
                  (selectedPriority && selectedPriority !== FILTER_VALUES.ALL) ||
                  (selectedAssignee && selectedAssignee !== FILTER_VALUES.ALL) ||
                  (selectedCreatedBy && selectedCreatedBy !== FILTER_VALUES.ALL) ||
                  (selectedStatus && selectedStatus !== FILTER_VALUES.ALL) ||
                  (selectedEpic && selectedEpic !== FILTER_VALUES.ALL && selectedEpic !== '' && viewMode === VIEW_MODES.GANTT) ||
                  Boolean(startDate) ||
                  Boolean(dueDate) ||
                  (selectedSort && selectedSort !== 'NEWEST_FIRST' && viewMode !== VIEW_MODES.GANTT && viewMode !== VIEW_MODES.CALENDAR)
                )
              }
              appliedCount={resolvedAppliedCount}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

FilterHeader.propTypes = {
  isScrumProject: PropTypes.bool,
};

export default FilterHeader;
